import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';
import Switch from '@mui/material/Switch';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import InsightsIcon from '@mui/icons-material/Insights';
import SettingsIcon from '@mui/icons-material/Settings';
import PaymentIcon from '@mui/icons-material/Payment';
import ShieldIcon from '@mui/icons-material/Shield';
import FeedbackIcon from '@mui/icons-material/Feedback';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PeopleIcon from '@mui/icons-material/People';
import BarChartIcon from '@mui/icons-material/BarChart';
import HistoryIcon from '@mui/icons-material/History';
import EmailIcon from '@mui/icons-material/Email';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import SecurityIcon from '@mui/icons-material/Security';
import SummarizeIcon from '@mui/icons-material/Summarize';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import GroupIcon from '@mui/icons-material/Group';
import BusinessIcon from '@mui/icons-material/Business';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import CloudIcon from '@mui/icons-material/Cloud';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import MapIcon from '@mui/icons-material/Map';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import PublicIcon from '@mui/icons-material/Public';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ChatIcon from '@mui/icons-material/Chat';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CampaignIcon from '@mui/icons-material/Campaign';
import WebhookIcon from '@mui/icons-material/Webhook';
import TuneIcon from '@mui/icons-material/Tune';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { useSite } from '../contexts/SiteContext';
import { useAuth } from '../contexts/AuthContext';

const DRAWER_WIDTH = 260;

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  tourId?: string;
}

// ─── Journey-oriented navigation ───

// OVERVIEW — always visible
const OVERVIEW_NAV: NavItem[] = [
  { label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon fontSize="small" /> },
];

// IMPROVE — the core user journey
const IMPROVE_NAV: NavItem[] = [
  { label: 'Recommendations', path: '/directives', icon: <AutoFixHighIcon fontSize="small" />, tourId: 'nav-recommendations' },
  { label: 'Progress', path: '/measurements', icon: <InsightsIcon fontSize="small" /> },
  { label: 'Pipeline', path: '/pipeline', icon: <AccountTreeIcon fontSize="small" /> },
  { label: 'Weekly Report', path: '/weekly-report', icon: <SummarizeIcon fontSize="small" /> },
  { label: 'Interaction Map', path: '/interaction-map', icon: <MapIcon fontSize="small" /> },
];

// MANAGE — site-specific tools (visible when site selected)
const MANAGE_NAV: NavItem[] = [
  { label: 'Hosting & Domain', path: '/hosting', icon: <CloudIcon fontSize="small" />, tourId: 'nav-hosting' },
  { label: 'Content', path: '/content', icon: <EditNoteIcon fontSize="small" /> },
  { label: 'Content Safety', path: '/guardrails', icon: <ShieldIcon fontSize="small" /> },
  { label: 'Customer Feedback', path: '/feedback-triage', icon: <FeedbackIcon fontSize="small" /> },
];

// CONFIGURE — collapsible sub-section under MANAGE
const CONFIGURE_NAV: NavItem[] = [
  { label: 'Location Rules', path: '/geo-rules', icon: <PublicIcon fontSize="small" /> },
  { label: 'Site Features', path: '/feature-toggles', icon: <ToggleOnIcon fontSize="small" /> },
  { label: 'AI Chat', path: '/chat-config', icon: <ChatIcon fontSize="small" /> },
  { label: 'Chat Training', path: '/knowledge-base', icon: <MenuBookIcon fontSize="small" /> },
  { label: 'Announcements', path: '/announcements', icon: <CampaignIcon fontSize="small" /> },
  { label: 'Webhooks', path: '/webhooks', icon: <WebhookIcon fontSize="small" /> },
];

// ACCOUNT — bottom section
const ACCOUNT_NAV: NavItem[] = [
  { label: 'Billing', path: '/billing', icon: <PaymentIcon fontSize="small" /> },
  { label: 'Settings', path: '/settings', icon: <SettingsIcon fontSize="small" /> },
];

// ADMIN — platform admins only
const ADMIN_NAV: NavItem[] = [
  { label: 'Admin Overview', path: '/admin-overview', icon: <AdminPanelSettingsIcon fontSize="small" /> },
  { label: 'All Sites', path: '/all-sites', icon: <PeopleIcon fontSize="small" /> },
  { label: 'Analytics', path: '/analytics', icon: <BarChartIcon fontSize="small" /> },
  { label: 'Audit Log', path: '/audit-log', icon: <HistoryIcon fontSize="small" /> },
  { label: 'Campaigns', path: '/campaigns', icon: <EmailIcon fontSize="small" /> },
  { label: 'Design Sessions', path: '/design-sessions', icon: <DesignServicesIcon fontSize="small" /> },
  { label: 'Impersonation', path: '/impersonation', icon: <SwitchAccountIcon fontSize="small" /> },
  { label: 'Roles & Access', path: '/rbac', icon: <SecurityIcon fontSize="small" /> },
  { label: 'Analyses', path: '/analyses', icon: <TravelExploreIcon fontSize="small" /> },
  { label: 'Demo Sites', path: '/demo-sites', icon: <TravelExploreIcon fontSize="small" /> },
  { label: 'Users', path: '/admin-users', icon: <GroupIcon fontSize="small" /> },
  { label: 'Organizations', path: '/admin-orgs', icon: <BusinessIcon fontSize="small" /> },
  { label: 'System Health', path: '/system-health', icon: <MonitorHeartIcon fontSize="small" /> },
];

interface SidebarProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ mobileOpen, onMobileClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { activeSiteId } = useSite();
  const { isPlatformAdmin, showAdmin, adminModeVisible, setAdminModeVisible } = useAuth();
  const [adminOpen, setAdminOpen] = useState(false);
  const [configureOpen, setConfigureOpen] = useState(false);

  const handleNav = (path: string) => {
    navigate(path);
    if (isMobile) onMobileClose();
  };

  const renderNavItem = (item: NavItem) => {
    const isSelected = location.pathname === item.path;
    return (
      <ListItemButton
        key={item.path}
        selected={isSelected}
        onClick={() => handleNav(item.path)}
        {...(item.tourId ? { 'data-tour': item.tourId } as any : {})}
        sx={{
          borderRadius: '8px',
          mb: 0.5,
          minHeight: 40,
          '&.Mui-selected': {
            backgroundColor: 'primary.main',
            color: '#fff',
            '&:hover': { backgroundColor: 'primary.dark' },
            '& .MuiListItemIcon-root': { color: '#fff' },
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }}
        />
      </ListItemButton>
    );
  };

  const sectionHeader = (label: string) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, pt: 1.5, pb: 0.5 }}>
      <Typography
        variant="overline"
        sx={{ color: 'text.secondary', fontSize: '0.65rem', letterSpacing: 1.5 }}
      >
        {label}
      </Typography>
    </Box>
  );

  const isAdminActive = ADMIN_NAV.some(item => location.pathname === item.path);

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo */}
      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          component="img"
          src="/logo.svg"
          alt="Patchwise"
          sx={{ width: 40, height: 40, borderRadius: 2 }}
        />
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            Patchwise
          </Typography>
          <Typography variant="caption" color="text.secondary">
            AI-Powered SEO
          </Typography>
        </Box>
      </Box>

      <Divider />

      {/* Main Navigation — journey-oriented */}
      <List sx={{ px: 1.5, flexGrow: 1 }}>
        {/* Overview */}
        {OVERVIEW_NAV.map(renderNavItem)}

        {/* Improve — the core journey */}
        <Divider sx={{ my: 1 }} />
        {sectionHeader('Improve')}
        {IMPROVE_NAV.map(renderNavItem)}

        {/* Manage — site tools (visible when site selected) */}
        {activeSiteId && (
          <>
            <Divider sx={{ my: 1 }} />
            {sectionHeader('Manage')}
            {MANAGE_NAV.map(renderNavItem)}

            {/* Configure — collapsible sub-section */}
            <ListItemButton
              onClick={() => setConfigureOpen(!configureOpen)}
              sx={{ borderRadius: '8px', mb: 0.5, minHeight: 36, mt: 0.5 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <TuneIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </ListItemIcon>
              <ListItemText
                primary="Configure"
                primaryTypographyProps={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary' }}
              />
              {configureOpen || CONFIGURE_NAV.some(item => location.pathname === item.path)
                ? <ExpandLessIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                : <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              }
            </ListItemButton>
            <Collapse in={configureOpen || CONFIGURE_NAV.some(item => location.pathname === item.path)}>
              <List disablePadding sx={{ pl: 1 }}>
                {CONFIGURE_NAV.map(renderNavItem)}
              </List>
            </Collapse>
          </>
        )}
      </List>

      {/* Account — bottom pinned */}
      <List sx={{ px: 1.5, pb: 0.5 }}>
        <Divider sx={{ mb: 1 }} />
        {ACCOUNT_NAV.map(renderNavItem)}
      </List>

      {/* Admin — very bottom, always visible for platform admins */}
      {isPlatformAdmin && (
        <Box sx={{ px: 1.5, pb: 0.5 }}>
          <Divider sx={{ mb: 0.5 }} />
          <ListItemButton
            onClick={() => {
              if (adminModeVisible) {
                setAdminOpen(!adminOpen);
              } else {
                setAdminModeVisible(true);
                setAdminOpen(true);
              }
            }}
            sx={{ borderRadius: '8px', mb: 0.5, minHeight: 36 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <AdminPanelSettingsIcon fontSize="small" sx={{ color: adminModeVisible ? 'primary.main' : 'text.disabled' }} />
            </ListItemIcon>
            <ListItemText
              primary="Admin"
              primaryTypographyProps={{
                fontWeight: 600,
                fontSize: '0.8rem',
                color: adminModeVisible ? 'text.primary' : 'text.disabled',
              }}
            />
            <Switch
              size="small"
              checked={adminModeVisible}
              onChange={(e) => {
                e.stopPropagation();
                const on = e.target.checked;
                setAdminModeVisible(on);
                if (on) setAdminOpen(true);
              }}
              onClick={(e) => e.stopPropagation()}
              sx={{ mr: 0.5 }}
            />
            {adminModeVisible && (
              adminOpen || isAdminActive
                ? <ExpandLessIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                : <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            )}
          </ListItemButton>
          {adminModeVisible && (
            <Collapse in={adminOpen || isAdminActive}>
              <List disablePadding sx={{ pl: 1 }}>
                {ADMIN_NAV.map(renderNavItem)}
              </List>
            </Collapse>
          )}
        </Box>
      )}

      {/* Version */}
      <Box sx={{ px: 2, pb: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary">
          v3.1.0
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: DRAWER_WIDTH,
            borderRight: '1px solid',
            borderColor: 'divider',
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Sidebar;
